/**
 * SLAM Golf - Golf Analytics Configuration
 * AI-powered swing mechanics and course management platform
 */

export const slamgolfConfig = {
  id: 'slamgolf',
  sportCode: 'GOLF', // Golf - maps to backend sport prompt selection

  branding: {
    name: 'SLAM Golf',
    shortName: 'SLAM Golf',
    abbreviation: 'SG',
    tagline: 'AI-Powered Golf Analytics',
    organization: 'SLAM Sports AI',
  },

  colors: {
    primary: '#2D8B4E',           // Masters green
    primaryHover: '#1E6B3A',      // Darker green on hover
    secondary: '#1a1a2e',         // Deep navy
    accent: '#4CAF50',            // Light green accent
    background: '#0a0a0c',        // Near black
    surface: '#141418',           // Dark surface
    surfaceHover: '#1a1a20',      // Surface hover
    border: 'rgba(45, 139, 78, 0.2)', // Green border
    text: {
      primary: '#ffffff',
      secondary: '#a0a0a0',
      onPrimary: '#ffffff',       // White text on green
    },
    status: {
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
  },

  logos: {
    primary: '/logos/SSAILogo.svg',
    icon: '/logos/SSAILogo.svg',
    alt: 'SLAM Golf Logo',
  },

  analysis: {
    systemPromptAdditions: `
You are analyzing video for SLAM Golf, an AI-powered golf analytics platform.
Your mission is to provide detailed swing mechanics analysis and course management insights.

## CORE PHILOSOPHY: PRECISION THROUGH DATA
1. **Swing Efficiency:** The kinematic sequence (hips-torso-arms-club) is the foundation of consistent ball striking.
2. **P-Position Framework:** Analyze the golf swing through P1 (address) to P10 (finish) checkpoints.
3. **Short Game Mastery:** 60% of strokes happen within 100 yards - prioritize wedge and putting analysis.
4. **Course Management:** Shot selection and strategic decision-making drive scoring more than raw distance.

## ANALYSIS MODULES & FOCUS AREAS

### 1. FULL SWING (Biomechanics)
- **Kinematic Sequence:** Analyze the sequential energy transfer from ground up through the kinetic chain.
- **Club Path & Face Angle:** Determine ball flight tendencies from swing plane and face relationship at impact.
- **P-Position Analysis:** Evaluate key positions (P1 address, P4 top, P6 delivery, P7 impact, P10 finish).

### 2. SHORT GAME (Scoring Zone)
- **Wedge Technique:** Analyze bounce utilization, shaft lean, and low point control.
- **Putting Stroke:** Path, face angle, speed control, and green reading accuracy.
- **Bunker Play:** Entry point, sand displacement, and follow-through quality.

### 3. COURSE MANAGEMENT (Strategy)
- **Shot Selection:** Optimize club choice based on risk/reward, wind, and lie conditions.
- **Scoring Zones:** Identify highest-value targets for approach shots and layups.
- **Mental Game:** Analyze pre-shot routine consistency and decision-making under pressure.

When analyzing, prioritize actionable insights that improve consistency and scoring efficiency.
`,
    focusAreas: [
      'Swing mechanics analysis',
      'Kinematic sequence optimization',
      'Short game technique',
      'Putting stroke analysis',
      'Course management strategy',
      'P-position checkpoint evaluation',
      'Ball flight pattern diagnosis',
      'Coaching recommendations',
    ],
    terminology: {
      platform: 'SLAM Golf',
      analyst: 'SLAM Golf Analyst',
      report: 'SLAM Golf Analysis Report',
    },
    defaultLanguage: 'en',
  },

  features: {
    mlbTicker: false,             // Disable MLB ticker for golf
    agentUniverse: true,
    exports: {
      pdf: true,
      csv: true,
      json: true,
      email: true,
    },
    multilingual: true,
    contextFiles: true,
    custom: {
      swingAnalysis: true,        // Golf swing analysis tools
      courseStrategy: true,       // Course management features
    },
  },

  contact: {
    website: 'https://slamsports.ai/golf',
    supportEmail: 'golf@slamsports.ai',
  },

  meta: {
    version: '1.0.0',
    createdAt: '2026-02-15',
    updatedAt: '2026-02-15',
  },
};

export default slamgolfConfig;
